import { paths } from "@minuthq/minut-api-types/schema"
import { UseQueryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import {
  paradiseLLMPromptsKeys,
  useParadiseLLMPromptsCache,
} from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueryCache"
import { TParadiseLLMPromptsQueryParams } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { TParadiseLLMPromptsResponse } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { TParadiseLLMPrompt } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import {
  useMinutDelete,
  useMinutPatch,
  useMinutPost,
  useMinutQuery,
} from "src/hooks/minutQuery"

export function useFetchParadiseLLMPrompts({
  filter,
  options,
}: {
  filter: TParadiseLLMPromptsQueryParams
  options?: UseQueryOptions<
    TParadiseLLMPromptsResponse,
    AxiosError,
    TParadiseLLMPromptsResponse,
    ReturnType<typeof paradiseLLMPromptsKeys.promptsList>
  >
}) {
  return useMinutQuery({
    queryKey: paradiseLLMPromptsKeys.promptsList(filter),
    queryPath: `/llm-prompts`,
    ...options,
  })
}

export function useFetchParadiseLLMPrompt({
  id,
  options,
}: {
  id: string
  options?: UseQueryOptions<
    TParadiseLLMPrompt,
    AxiosError,
    TParadiseLLMPrompt,
    ReturnType<typeof paradiseLLMPromptsKeys.prompt>
  >
}) {
  return useMinutQuery<"/llm-prompts/{prompt_id}">({
    queryKey: paradiseLLMPromptsKeys.prompt(id),
    queryPath: `/llm-prompts/${id}`,
    ...options,
  })
}

export function usePostParadiseLLMPrompt() {
  const paradiseLLMPromptsCache = useParadiseLLMPromptsCache()

  return useMinutPost<"/llm-prompts">({
    apiPath: "/llm-prompts",
    pathFn: () => "/llm-prompts",
    options: {
      onSuccess: () => {
        return paradiseLLMPromptsCache.invalidateParadiseLLMPromptsList()
      },
    },
  })
}

export function useDeleteParadiseLLMPrompt() {
  const paradiseLLMPromptsCache = useParadiseLLMPromptsCache()

  return useMinutDelete<"/llm-prompts/{prompt_id}">({
    apiPath: "/llm-prompts/{prompt_id}",
    pathFn: (variables) => `/llm-prompts/${variables.prompt_id}`,
    options: {
      onSuccess: () => {
        return paradiseLLMPromptsCache.invalidateParadiseLLMPromptsList()
      },
    },
  })
}

export function usePostParadiseLLMPromptVersion() {
  const paradiseLLMPromptsCache = useParadiseLLMPromptsCache()

  return useMinutPost<"/llm-prompts/{prompt_id}/versions">({
    apiPath: "/llm-prompts/{prompt_id}/versions",
    pathFn: (variables) => `/llm-prompts/${variables.prompt_id}/versions`,
    options: {
      onSuccess: (_, variables) => {
        return paradiseLLMPromptsCache.invalidateParadiseLLMPrompt(
          variables.path.prompt_id
        )
      },
    },
  })
}

export function usePatchParadiseLLMPrompt() {
  const paradiseLLMPromptsCache = useParadiseLLMPromptsCache()

  return useMinutPatch<"/llm-prompts/{prompt_id}">({
    apiPath: "/llm-prompts/{prompt_id}",
    pathFn: (variables) => `/llm-prompts/${variables.prompt_id}`,
    options: {
      onSuccess: (_, variables) => {
        return Promise.all([
          paradiseLLMPromptsCache.invalidateParadiseLLMPromptsList(),
          paradiseLLMPromptsCache.invalidateParadiseLLMPrompt(
            variables.path.prompt_id
          ),
        ])
      },
    },
  })
}

export function usePostParadiseLLMPromptTestMessage() {
  return useMinutPost<
    "/llm-prompts/{prompt_id}/test",
    400 | 401 | 403 | 404,
    paths["/llm-prompts/{prompt_id}/test"]["post"]["responses"]["200"]["content"]["application/json"]
  >({
    apiPath: "/llm-prompts/{prompt_id}/test",
    pathFn: (variables) => `/llm-prompts/${variables.prompt_id}/test`,
  })
}
